import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  EventInput,
  EventUpdate,
  PublicEvent,
  RsvpRequest,
  RsvpEntry,
} from "@vedaunion/shared";
import { apiFetch, ApiError } from "./api";

// -----------------------------------------------------------------------------
// React Query hooks for the calendar.
// -----------------------------------------------------------------------------

const EVENT_LIST_KEY = (params: ListParams) => ["events", "list", params] as const;
const EVENT_UPCOMING_KEY = (params: UpcomingParams) => ["events", "upcoming", params] as const;
const EVENT_DETAIL_KEY = (slug: string) => ["events", "detail", slug] as const;
const EVENT_RSVPS_KEY = (id: number) => ["events", "rsvps", id] as const;

export interface ListParams {
  from?: string;
  to?: string;
  category?: string;
  upcoming?: boolean;
  limit?: number;
  all?: boolean;
  /** When true (default for date-range queries), recurring events are
   *  expanded into individual occurrences. */
  expand?: boolean;
}

export interface UpcomingParams {
  limit?: number;
  days?: number;
  category?: string;
}

function buildListQuery(params: ListParams): string {
  const sp = new URLSearchParams();
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.category) sp.set("category", params.category);
  if (params.upcoming) sp.set("upcoming", "true");
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.all) sp.set("all", "true");
  if (params.expand === false) sp.set("expand", "false");
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function buildUpcomingQuery(params: UpcomingParams): string {
  const sp = new URLSearchParams();
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.days) sp.set("days", String(params.days));
  if (params.category) sp.set("category", params.category);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useEvents(params: ListParams = {}) {
  return useQuery<{ events: PublicEvent[] }>({
    queryKey: EVENT_LIST_KEY(params),
    queryFn: ({ signal }) =>
      apiFetch<{ events: PublicEvent[] }>(`/api/events${buildListQuery(params)}`, { signal }),
  });
}

/** Flat list of upcoming occurrences (already recurrence-expanded server-side).
 *  Use for landing-page strips, agenda views, "what's next" widgets. */
export function useUpcomingEvents(params: UpcomingParams = {}) {
  return useQuery<{ events: PublicEvent[] }>({
    queryKey: EVENT_UPCOMING_KEY(params),
    queryFn: ({ signal }) =>
      apiFetch<{ events: PublicEvent[] }>(`/api/events/upcoming${buildUpcomingQuery(params)}`, { signal }),
  });
}

export function useEvent(slug: string | undefined) {
  return useQuery<{ event: PublicEvent }>({
    queryKey: EVENT_DETAIL_KEY(slug ?? ""),
    queryFn: ({ signal }) => apiFetch<{ event: PublicEvent }>(`/api/events/${slug}`, { signal }),
    enabled: !!slug,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation<{ event: PublicEvent }, ApiError, EventInput>({
    mutationFn: (body) => apiFetch<{ event: PublicEvent }>("/api/events", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useUpdateEvent(id: number) {
  const qc = useQueryClient();
  return useMutation<{ event: PublicEvent }, ApiError, EventUpdate>({
    mutationFn: (body) => apiFetch<{ event: PublicEvent }>(`/api/events/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (id) => apiFetch<{ ok: true; deleted: number }>(`/api/events/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDuplicateEvent() {
  const qc = useQueryClient();
  return useMutation<{ event: PublicEvent }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ event: PublicEvent }>(`/api/events/${id}/duplicate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useRsvp(id: number) {
  const qc = useQueryClient();
  return useMutation<{ event: PublicEvent }, ApiError, RsvpRequest>({
    mutationFn: (body) => apiFetch<{ event: PublicEvent }>(`/api/events/${id}/rsvp`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useClearRsvp(id: number) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () => apiFetch<{ ok: true }>(`/api/events/${id}/rsvp`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

/** Organiser view of all RSVPs for an event. */
export function useEventRsvps(id: number | null) {
  return useQuery<{ rsvps: RsvpEntry[] }>({
    queryKey: EVENT_RSVPS_KEY(id ?? 0),
    queryFn: ({ signal }) => apiFetch<{ rsvps: RsvpEntry[] }>(`/api/events/${id}/rsvps`, { signal }),
    enabled: id !== null && id > 0,
  });
}

/** URL helpers for ICS export — use directly as anchor href. */
export const eventIcsHref = (slug: string) => `/api/events/${slug}.ics`;
export const calendarFeedIcsHref = () => `/api/events/feed.ics`;
